import { View, Text, StyleSheet, Pressable } from "react-native";
import WebPage from "@/ui/WebPage";
import Icon, { IconType } from "@/ui/Icon";
import { Colors } from "@/constants/Colors";
import { ms } from "@/ui/moderateScale";
import Heading from "@/ui/Heading";
import SelectorButton from "@/ui/SelectorButton";
import NoteItem from "@/ui/NoteItem";
import { useUISlice } from "@/models/useUISlice";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/models/useAuth";
import { useUserSlice } from "@/models/useUserSlice";
import { useEffect, useState } from "react";
import Menu from "@/ui/Menu";
import { Folder } from "@/models/types";
import { useRouter } from "@/_router";
import { getBasePath } from "@/models/utils";
import { App } from "@capacitor/app";
import AlertWidgetCreateNote from "@/ui/AlertWidgetCreateNote";
import { logFunnelEvent } from "@/fetchData/datadog";
import { FREE_MAX_NOTES_COUNT } from "@/constants";
import List from "@/ui/List";

export default function Home() {
  const { t } = useTranslation();
  const allNotes = useUserSlice((state) => state.notes);
  const folders = useUserSlice((state) => state.folders);
  const router = useRouter();
  const selectedFolder = useUISlice((state) => state.selectedFolder);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const insets = useUISlice((state) => state.device.safeArea);
  const notes = selectedFolder?.notes ?? allNotes;
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    useUserSlice
      .getState()
      .user()
      .profile((nextToken) => {
        setNextToken(nextToken);
      });

    logFunnelEvent("[0] - page visited");

    const listenBackButton = () => {
      App.addListener("backButton", () => {
        App.minimizeApp();
      });
    };

    listenBackButton();

    if (selectedFolder) {
      useUserSlice
        .getState()
        .folder()
        .listNotes(selectedFolder.folder.id)
        .then((notes) => {
          useUISlice.getState().setSelectedFolder({
            folder: selectedFolder.folder,
            notes,
          });
        });
    }

    return () => {
      App.removeAllListeners();
    };
  }, []);

  const onSelectFolder = (folder?: Folder) => {
    if (!folder) {
      useUISlice.getState().setSelectedFolder(undefined);
      return;
    }

    useUISlice.getState().setSelectedFolder({
      folder,
      notes: [],
    });
    useUserSlice
      .getState()
      .folder()
      .listNotes(folder?.id)
      .then((notes) => {
        useUISlice.getState().setSelectedFolder({ folder, notes });
      });
  };

  const onNotePress = (noteId: string) => {
    router.push(`${getBasePath()}/note?id=${noteId}`);
  };

  const _signIn = () => {
    useAuth.signIn({});
  };

  const onEndReached = () => {
    if (!nextToken) return;

    if (!selectedFolder) {
      useUserSlice
        .getState()
        .note()
        .list(nextToken, (token) => {
          setNextToken(token);
        });
    }
  };

  return (
    <>
      <WebPage tab="home">
        <Home.FreeNotesCounter />
        <View style={styles.container}>
          {!isMobile && (
            <View>
              <Heading
                text={t("screens.home.newNote")}
                style={styles.heading}
              />
              <Text style={styles.subtitle}>
                {t("screens.home.subtitleFree", {
                  count: FREE_MAX_NOTES_COUNT,
                })}
              </Text>

              <AlertWidgetCreateNote.ActionsList />
            </View>
          )}

          <View style={styles.notesContainer}>
            {!isMobile && (
              <Heading
                text={t("screens.home.myNotes")}
                style={styles.notesHeading}
              />
            )}

            <View
              style={StyleSheet.flatten([
                styles.searchRow,
                isMobile && {
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: ms(12),
                },
              ])}
            >
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Menu
                  position="bottom left"
                  key={
                    selectedFolder
                      ? `${selectedFolder?.folder.id}-${selectedFolder?.folder.name}`
                      : folders.map((folder) => folder.name).join("-")
                  }
                  actions={[
                    {
                      text: `📂 ${t("screens.home.allNotes")}`,
                      onPress: () => onSelectFolder(undefined),
                      notificationText: selectedFolder ? undefined : "✓",
                      iconStyle: { width: ms(18), height: ms(18) },
                    },
                    ...folders.map((folder) => ({
                      text: `📂 ${folder.name}`,
                      onPress: () => onSelectFolder(folder),
                      notificationText:
                        selectedFolder?.folder.id === folder.id
                          ? "✓"
                          : undefined,
                    })),
                    { divider: true },
                    {
                      icon: "plus" as IconType,
                      text: t("screens.home.newFolder"),
                      iconStyle: {
                        width: ms(24),
                        height: ms(24),
                        tintColor: Colors.text,
                      },
                      onPress: () => useUserSlice.getState().folder().create(),
                    },
                    folders.length > 0 && {
                      icon: "pen" as IconType,
                      text: t("screens.home.manageFolders"),
                      iconStyle: {
                        tintColor: Colors.text,
                      },
                      onPress: () =>
                        useUISlice.getState().setAlertWidget("manageFolders"),
                    },
                  ].filter(Boolean)}
                  actionComponent={
                    <SelectorButton
                      rightIcon="caretDownFilled"
                      text={
                        selectedFolder
                          ? `📂 ${selectedFolder.folder.name}`
                          : `📂 ${t("screens.home.allNotes")}`
                      }
                    />
                  }
                />

                {selectedFolder &&
                  selectedFolder?.folder?.listNotesIds?.length > 0 && (
                    <Pressable
                      style={{
                        flexDirection: "row",
                        gap: ms(8),
                        alignItems: "center",
                        marginLeft: ms(16),
                      }}
                      onPress={() =>
                        useUISlice
                          .getState()
                          .setAlertWidgetChat({ folder: selectedFolder.folder })
                      }
                    >
                      <Text
                        style={{
                          fontSize: ms(14),
                          color: Colors.accent,
                          fontWeight: "600",
                        }}
                      >
                        {t("screens.home.chatWithFolder")}
                      </Text>
                    </Pressable>
                  )}
              </View>

              <Pressable
                style={StyleSheet.flatten([
                  styles.searchBar,
                  isMobile && { width: "100%", maxWidth: "100%" },
                ])}
                onPress={() =>
                  useUISlice.getState().setAlertWidgetSearch({
                    placeholder: selectedFolder
                      ? t("screens.home.searchNotesInFolder", {
                          folderName: selectedFolder.folder.name,
                        })
                      : t("screens.home.searchAnyNote"),
                    folderId: selectedFolder?.folder.id,
                  })
                }
              >
                <Icon icon="searchBar" style={styles.searchIcon} />
                <Text numberOfLines={1} style={styles.searchText}>
                  {selectedFolder
                    ? t("screens.home.searchNotesInFolder", {
                        folderName: selectedFolder.folder.name,
                      })
                    : t("screens.home.searchAnyNote")}
                </Text>
              </Pressable>
            </View>

            <View style={styles.noteItemSpacer} />

            {!useAuth.isAuthenticated() && (
              <View style={styles.signInPrompt}>
                <Text style={styles.signInText}>
                  {t("screens.home.loginText")}
                  {"\n"}
                  <Text
                    onPress={_signIn}
                    style={{ color: Colors.accent, fontWeight: "500" }}
                  >
                    {t("screens.home.loginButton")}
                  </Text>
                </Text>
              </View>
            )}

            <List
              data={notes}
              keyExtractor={(item) => item.id}
              onEndReached={onEndReached}
              renderItem={({ item }) => (
                <NoteItem
                  key={item.id}
                  noteId={item.id}
                  onPress={() => onNotePress(item.id)}
                />
              )}
            />
          </View>
        </View>
      </WebPage>

      {isMobile && (
        <View
          style={{
            width: "100%",
            bottom: insets.bottom + ms(24),
            height: ms(72),
            alignItems: "center",
            flexDirection: "row",
            ...({ position: "fixed" } as any),
          }}
        >
          <Pressable
            style={{
              backgroundColor: Colors.softRed,
              width: ms(72),
              height: ms(72),
              borderRadius: ms(100),
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              left: "50%",
              transform: [{ translateX: -ms(36) }],
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 3.84,
            }}
            onPress={() =>
              useUISlice.getState().setAlertWidget("voice_recording")
            }
          >
            <Icon
              icon="mic"
              style={{
                width: ms(24),
                height: ms(24),
                tintColor: Colors.white,
              }}
            />
          </Pressable>

          <Pressable
            style={{
              backgroundColor: Colors.white,
              width: ms(48),
              height: ms(48),
              borderRadius: ms(100),
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              left: "50%",
              transform: [{ translateX: -ms(8) }],
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 3.84,
            }}
            onPress={() => useUISlice.getState().setAlertWidget("createNote")}
          >
            <Icon
              icon="caretRight"
              style={{
                transform: [{ rotate: "90deg" }],
                width: ms(24),
                height: ms(24),
                tintColor: Colors.text,
              }}
            />
          </Pressable>
        </View>
      )}
    </>
  );
}

Home.FreeNotesCounter = () => {
  const { t } = useTranslation();
  const isMobile = useUISlice((state) => state.device.isMobile);
  const userNotesCount = useUserSlice.getState()?.userData?.notesCount || 0;
  const localNotesCount = useUserSlice((state) => state.notes.length);
  const notesCount = Math.max(userNotesCount, localNotesCount);
  const paywallVisible = useUISlice((state) => state.paywallVisible);
  const [isVisible, setIsVisible] = useState(
    !useUserSlice.getState().isSubscribed
  );
  const freeNotesCount = Math.max(0, FREE_MAX_NOTES_COUNT - notesCount);

  useEffect(() => {
    useUserSlice
      .getState()
      .subscription()
      .status()
      .then(() => {
        if (useAuth.isAuthenticated()) {
          setIsVisible(!useUserSlice.getState().isSubscribed);
        }
      });
  }, [paywallVisible]);

  if (!isVisible) return null;

  return (
    <Pressable
      onPress={() => useUISlice.getState().setPaywallVisible(true)}
      style={{
        position: "relative",
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: Colors.error + "20",
        paddingVertical: ms(12),
        marginBottom: ms(16),
        marginHorizontal: -ms(16),
        paddingHorizontal: ms(16),
        alignItems: "center",
        justifyContent: "center",
        ...(!isMobile && {
          marginHorizontal: 0,
        }),
      }}
    >
      <Text
        style={{
          color: Colors.text,
          fontWeight: "400",
          fontSize: ms(14),
          textAlign: "center",
        }}
      >
        {freeNotesCount > 0
          ? t("screens.home.freeNotesCount", { count: freeNotesCount })
          : t("screens.home.freeNotesCountNoRemaining", {
              count: FREE_MAX_NOTES_COUNT,
            })}{" "}
        <Text
          style={{
            color: Colors.accent,
            fontWeight: "500",
            textDecorationLine: "underline",
            textDecorationStyle: "dotted",
          }}
        >
          {t("screens.home.upgrade")}
        </Text>
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  heading: {
    marginBottom: ms(6),
  },
  subtitle: {
    fontSize: ms(14),
    lineHeight: ms(20),
    color: Colors.subtitle,
    marginBottom: ms(16),
  },
  optionsContainer: {
    gap: ms(16),
  },
  row: {
    flexDirection: "row",
    gap: ms(16),
  },
  optionCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.white,
    marginRight: ms(12),
    borderRadius: ms(16),
    paddingVertical: ms(16),
    paddingHorizontal: ms(16),
    flex: 1,
    cursor: "pointer",
  },
  iconContainer: {
    width: ms(42),
    height: ms(42),
    borderRadius: ms(21),
    backgroundColor: Colors.gray3,
    alignItems: "center",
    justifyContent: "center",
    marginRight: ms(12),
  },
  icon: {
    width: ms(20),
    height: ms(20),
  },
  optionText: {
    fontSize: ms(16),
    lineHeight: ms(20),
    fontWeight: "500",
    color: Colors.text,
  },
  spacer: {
    height: ms(32),
  },
  notesContainer: {
    flex: 1,
    paddingBottom: ms(124),
  },
  notesHeading: {
    marginBottom: ms(8),
  },
  searchRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  searchBar: {
    maxWidth: ms(300),
    width: "100%",
    backgroundColor: Colors.text + "10",
    paddingHorizontal: ms(16),
    height: ms(48),
    borderRadius: ms(100),
    flexDirection: "row",
    alignItems: "center",
    gap: ms(8),
  },
  searchIcon: {
    width: ms(20),
    height: ms(20),
    tintColor: Colors.subtitle,
  },
  searchText: {
    fontSize: ms(16),
    color: Colors.subtitle,
  },
  noteItemSpacer: {
    height: ms(16),
  },
  signInPrompt: {
    flexDirection: "row",
    gap: ms(8),
    padding: ms(24),
    paddingVertical: ms(24),
    backgroundColor: Colors.white,
    borderRadius: ms(16),
    alignItems: "center",
  },
  signInText: {
    color: Colors.subtitle,
    fontSize: ms(16),
    lineHeight: ms(24),
  },
});
